import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSupabase } from '../utils/supabase';
import BlogSearch from './BlogSearch';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  slug: string;
  created_at: string;
  publish_date: string;
  status: string;
  cover_image?: string;
  cover_image_alt?: string;
  tags: string[];
  categories: string[];
  view_count: number;
  author: string;
}

interface BlogListProps {
  onVisit?: () => void;
}

const BlogList: React.FC<BlogListProps> = ({ onVisit }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { supabase } = useSupabase();

  // Add state for search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data, error: supabaseError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .order('publish_date', { ascending: false });

        if (supabaseError) {
          throw new Error(`Failed to fetch posts: ${supabaseError.message}`);
        }

        if (!data) {
          throw new Error('No data received from Supabase');
        }

        // Update images to use more contemporary healthcare settings
        const updatedPosts = data.map(post => ({
          ...post,
          cover_image: post.cover_image?.includes('unsplash') 
            ? post.cover_image 
            : 'https://images.unsplash.com/photo-1666214280557-f1b5022eb634?ixlib=rb-1.2.1&auto=format&fit=crop&w=2070&q=80'
        }));

        setPosts(updatedPosts);
        
        // Extract unique tags and categories
        const tags = Array.from(new Set(data.flatMap(post => post.tags || [])));
        const categories = Array.from(new Set(data.flatMap(post => post.categories || [])));
        
        setAvailableTags(tags);
        setAvailableCategories(categories);
        
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError(err instanceof Error ? err.message : 'Error fetching posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
    onVisit?.();
  }, [supabase, onVisit]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterTags = (tags: string[]) => {
    setSelectedTags(tags);
  };

  const handleFilterCategories = (categories: string[]) => {
    setSelectedCategories(categories);
  };

  // Filter posts based on search and filters
  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTags = selectedTags.length === 0 || 
      selectedTags.some(tag => post.tags?.includes(tag));

    const matchesCategories = selectedCategories.length === 0 || 
      selectedCategories.some(category => post.categories?.includes(category));

    return matchesSearch && matchesTags && matchesCategories;
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white rounded-lg shadow-md p-4">
                <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">
          <h2 className="text-2xl font-bold mb-4">Error Loading Posts</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8">Latest Healthcare Insights</h1>
      
      <BlogSearch
        onSearch={handleSearch}
        onFilterTags={handleFilterTags}
        onFilterCategories={handleFilterCategories}
        availableTags={availableTags}
        availableCategories={availableCategories}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPosts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full"
          >
            {post.cover_image && (
              <div className="h-48 overflow-hidden">
                <img
                  src={post.cover_image}
                  alt={post.cover_image_alt || `Cover image for article: ${post.title}`}
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}
            <div className="p-6 flex flex-col flex-grow">
              <h2 className="text-xl font-semibold mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                {post.title}
              </h2>
              <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
              <div className="mt-auto">
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags?.map(tag => (
                    <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <span>{new Date(post.publish_date || post.created_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span>{post.view_count} views</span>
                    {post.categories?.[0] && (
                      <span className="text-blue-600">{post.categories[0]}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default BlogList;